import {
  CRYSTAL_MATERIAL_RANGES,
  DEFAULT_CRYSTAL_MATERIAL,
  getCrystalModelDefaults,
} from "@/lib/laser-physics";
import {
  CrystalShellConfig,
  CrystalShellResult,
  CrystalModelType,
} from "@/types/laser-engraving";
import { useControls } from "leva";
import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * 水晶外壳管理Hook
 *
 * 提供水晶外壳材质参数的完整管理功能，包括：
 * - Leva控制面板集成
 * - 基于真实水晶物理特性的默认配置
 * - 参数验证和边界检查
 * - 重置功能和预设配置
 *
 * @param panelName - 控制面板名称
 * @param initialConfig - 初始配置（可选）
 * @returns 水晶外壳配置和控制函数
 */
export function useCrystalShell(
  panelName: string = "Crystal Settings",
  initialConfig?: Partial<CrystalShellConfig>,
): CrystalShellResult {
  // 合并初始配置
  const effectiveConfig = useMemo(() => {
    return { ...DEFAULT_CRYSTAL_MATERIAL, ...initialConfig };
  }, [initialConfig]);

  // 用于跟踪上一次的 modelType，避免无限循环
  const previousModelTypeRef = useRef(effectiveConfig.modelType);

  // 🎯 Leva控制面板配置 - 基于真实水晶物理特性
  // 使用函数形式来获取 set 函数，用于动态更新配置
  const [config, set] = useControls(panelName, () => ({
    modelType: {
      value: effectiveConfig.modelType,
      options: [
        "cube",
        "crystal_1",
        "crystal_2",
        "crystal_3",
        "crystal_4",
      ] as CrystalModelType[],
    },
    transmission: {
      value: effectiveConfig.transmission,
      ...CRYSTAL_MATERIAL_RANGES.transmission,
    },
    roughness: {
      value: effectiveConfig.roughness,
      ...CRYSTAL_MATERIAL_RANGES.roughness,
    },
    ior: {
      value: effectiveConfig.ior,
      ...CRYSTAL_MATERIAL_RANGES.ior,
    },
    chromaticAberration: {
      value: effectiveConfig.chromaticAberration,
      ...CRYSTAL_MATERIAL_RANGES.chromaticAberration,
    },
    distortion: {
      value: effectiveConfig.distortion,
      ...CRYSTAL_MATERIAL_RANGES.distortion,
    },
    anisotropy: {
      value: effectiveConfig.anisotropy,
      ...CRYSTAL_MATERIAL_RANGES.anisotropy,
    },
    color: {
      value: effectiveConfig.color,
    },
    size: {
      value: effectiveConfig.size,
      ...CRYSTAL_MATERIAL_RANGES.size,
    },
    thickness: {
      value: effectiveConfig.thickness,
      ...CRYSTAL_MATERIAL_RANGES.thickness,
    },
  }));

  // 监听 modelType 变化，自动应用对应的默认配置
  useEffect(() => {
    if (config.modelType !== previousModelTypeRef.current) {
      console.log(
        "🎯 模型类型切换:",
        previousModelTypeRef.current,
        "->",
        config.modelType,
      );

      // 获取新模型类型的默认配置
      const newDefaults = getCrystalModelDefaults(config.modelType);

      // 应用新的默认配置到控制面板
      set({
        transmission: newDefaults.transmission,
        roughness: newDefaults.roughness,
        ior: newDefaults.ior,
        chromaticAberration: newDefaults.chromaticAberration,
        distortion: newDefaults.distortion,
        anisotropy: newDefaults.anisotropy,
        color: newDefaults.color,
        size: newDefaults.size,
        thickness: newDefaults.thickness,
      });

      // 更新引用
      previousModelTypeRef.current = config.modelType;
    }
  }, [config.modelType, set]);

  // 重置为当前模型类型的默认值
  const resetToDefaults = useCallback(() => {
    console.log("🎯 重置为", config.modelType, "的默认配置");
    const defaults = getCrystalModelDefaults(config.modelType);

    set({
      transmission: defaults.transmission,
      roughness: defaults.roughness,
      ior: defaults.ior,
      chromaticAberration: defaults.chromaticAberration,
      distortion: defaults.distortion,
      anisotropy: defaults.anisotropy,
      color: defaults.color,
      size: defaults.size,
      thickness: defaults.thickness,
    });
  }, [config.modelType, set]);

  return {
    config: config as CrystalShellConfig,
    resetToDefaults,
  };
}

// 类型导出，方便其他文件使用
export type { CrystalShellConfig, CrystalShellResult };
